/**
 * The homefront: when the strategic map sends a host at the city you actually
 * built.
 *
 * This is the join that makes the whole thing one game rather than three.
 *
 * Everywhere else, a `PendingBattle` becomes a field — two mustered armies on
 * flat ground, fought and forgotten. That is the right answer for a clash in
 * somebody else's border province. It is the *wrong* answer for an assault on
 * your capital, because your capital is not an abstraction: it is the town you
 * have spent the campaign laying out, with the walls you paid for on the ground
 * you chose to put them on. Fighting that battle on a generic field would throw
 * away the only thing the city-builder half of the game produces.
 *
 * So an assault on the player's home province is fought *in the home world*.
 * The attacking army's regiments become the wave; the walls, towers, districts
 * and garrison are whatever the player has actually built; and the result is
 * read back out of the same two numbers the tactical layer reports, so the
 * campaign cannot tell the difference.
 *
 * Nothing here is new state. It sets the same `incoming*` fields the director
 * writes and then calls the same `beginSiege`, which is why every siege fix
 * applies to both — and why an assault survives none of the director's
 * adaptive counter-picking, which would be cheating: the host on the map is the
 * host that arrives.
 */

import { Phase, Team, UF } from '../types.ts';
import { unitType } from '../../data/units.ts';
import { BUILDING_TYPES } from '../../data/buildings.ts';
import { MAX_AGE } from '../../data/ages.ts';
import { MAX_BARRACKS_POINTS } from './garrisons.ts';
import { statsOf, type World } from '../world.ts';
import { beginSiege } from '../systems/director.ts';
import { createCompany, fillCompany } from '../systems/companies.ts';
import { LogTone, pushLog } from '../events.ts';
import { nextInt } from '../../core/rng.ts';
import type { Regiment } from './types.ts';

/** What the map hands the city when a host arrives at its gates. */
export interface HomeAssault {
  /** Nation index doing the attacking, for the report and the log. */
  nation: number;
  factionId: string;
  nationName: string;
  regiments: Regiment[];
  /**
   * Regiments the player put **inside these walls** on the strategic map.
   *
   * The other half of *"you can choose army to hide in the walls"*, and without
   * it the feature is a lie on the one map the player actually cares about: an
   * assault on your own capital is fought in the home world, so a host that is
   * "inside the walls" but is not standing on the ground when the horns go has
   * not defended anything. `musterShelteredDefenders` puts the men on the field.
   *
   * They cost nothing here because they were bought on the map — `raiseCost`
   * took the gold and the iron months ago — and they take their losses back
   * through the ordinary battle result: `openBattle` names the sheltered host as
   * the defending army, so `applyBattleResult` applies `playerSurvive` to it
   * exactly as it would to a host that fought in a field.
   */
  defenders: Regiment[];
}

/** What the city hands back when the dust settles. */
export interface HomeAssaultResult {
  decided: boolean;
  playerWon: boolean;
  /** 0..1 of the models each side had when the horns sounded. */
  playerSurvive: number;
  enemySurvive: number;
}

/**
 * Models on the field at the moment the assault began, so survival can be
 * reported as a fraction. Kept outside `World` on purpose: like a battle, an
 * assault is played in one sitting and is not part of the saved state — see
 * the note on saving in `main.ts`.
 */
export interface HomeAssaultSnapshot {
  assault: HomeAssault;
  playerAtStart: number;
  enemyAtStart: number;
}

/**
 * Tell the realm what the city has become.
 *
 * The strategic layer scores a province on its garrison, its walls and its
 * wealth — none of which move when the player spends an evening laying out a
 * fourth quarter. So the city is written back into the province's
 * `development`, which is the one number the map already understands, and which
 * `provinceValue` multiplies by. The consequence is the loop the whole game is
 * built on: a settlement that grows into a real city becomes the richest prize
 * on the board, and the neighbours notice.
 *
 * Called whenever the player leaves the city, which is the only moment the two
 * halves are both in hand.
 */
/**
 * The capital's measure, as numbers rather than a write.
 *
 * Split out so a networked run can put the same arithmetic through the
 * command log instead of changing a shared world behind the other players'
 * backs — see `Cmd.SetCapitalStanding`.
 */
export function capitalMeasure(city: World): {
  development: number;
  fort: number;
  barracks: number;
  age: number;
} {
  let standing = 0;
  /**
   * The garrison half: what the town has built that a garrison comes out of.
   *
   * *"you can increase it with buildings and age"*, and this is the buildings.
   * Barracks, ranges, towers and bastions — everything the catalogue files under
   * `military` or `defence` — count, weighted by their upgrade tier exactly as
   * `development` weights everything else, and by their **Age gate**, because a
   * Knell Tower is not a palisade watchpost and the table already says so in
   * `BuildingType.tier`.
   *
   * Read off the catalogue's own categories rather than off a list of ids, for
   * the reason `deadfall.ts` reads the cheapest camp off `availableBuildings`: a
   * building added later cannot quietly fall outside the rule, and a faction
   * that garrisons out of something else is served by the same arithmetic.
   */
  let works = 0;
  const b = city.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.build[i] < 1) continue;
    if (b.team[i] !== Team.Player) continue;
    standing += 1 + (b.tier[i] - 1) * 0.5;
    const def = BUILDING_TYPES[b.typeId[i]];
    if (def && (def.category === 'military' || def.category === 'defence')) {
      works += (1 + (b.tier[i] - 1) * 0.5) * (0.6 + def.tier * 0.4);
    }
  }
  // A hamlet is 1, which is where every province starts; a city with two
  // hundred souls and forty buildings is around 3.5. Capped so the AI's target
  // scoring cannot be driven to absurdity by a very long game.
  const development = Math.min(4, 1 + city.pop / 130 + standing / 34);
  // Walls the player actually built are walls the map should know about, up to
  // the fortification ceiling the strategic layer works in.
  let wallTiles = 0;
  for (let k = 0; k < city.map.wall.length; k++) if (city.map.wall[k] !== 0) wallTiles++;
  return {
    development,
    fort: Math.min(3, Math.floor(wallTiles / 45)),
    // Clamped here as well as at the command, because the direct path in
    // `syncCapitalDevelopment` does not go through the command's own clamp and
    // two guards on a number crossing a layer boundary is one guard too few.
    barracks: Math.min(MAX_BARRACKS_POINTS, Math.round(works * 6)),
    age: city.age,
  };
}

/**
 * Which holding a settlement world is the settlement *of*.
 *
 * `WorldConfig.province` is written by `holdConfigFor` and is absent on every
 * world made before a crown could hold two towns — which is exactly the set of
 * worlds that are the capital, so the fallback is not a guess. Exported because
 * `main.ts` needs the same answer when it decides which town it is looking at.
 */
export function holdingOf(c: { capital: number }, city: World): number {
  return city.config.province ?? c.capital;
}

export function syncCapitalDevelopment(campaign: World, city: World): void {
  const c = campaign.campaign;
  if (!c) return;
  /**
   * …and it is the province this town actually stands on.
   *
   * This read was `c.capital` and was right for as long as a campaign had one
   * settlement world. With several, a player walking out of a captured hamlet
   * would have written that hamlet's development, walls and garrison works onto
   * their **capital's** province — a second town making the seat look poorer
   * every time you visited it, and the AI's target scoring reading the wrong
   * prize. One line, and it is the whole of what multiplicity costs here.
   */
  const prov = c.provinces[holdingOf(c, city)];
  if (!prov) return;
  const m = capitalMeasure(city);
  prov.development = m.development;
  prov.fort = Math.max(prov.fort, m.fort);
  /**
   * The two the garrison rule needs.
   *
   * `barracks` is a plain write rather than a `Math.max` — unlike the walls, a
   * garrison building that has been demolished or burnt down is a garrison that
   * is not there, and remembering the best month the city ever had would make
   * the map lie about a town that has just been sacked.
   *
   * The Age is the realm's, not the province's. See `CampaignState.age` for why
   * there is one Age on this board and not seventy-four.
   */
  prov.barracks = m.barracks;
  // …and only the seat's Age is the realm's. The same guard `doSetWatch` has,
  // for the same reason: this is a plain write, and a player walking out of a
  // newly captured hamlet must not reset the whole board to the first Age.
  if (holdingOf(c, city) === c.capital) {
    c.age = Math.max(1, Math.min(MAX_AGE, Math.round(m.age)));
  }
}

/** Player soldiers standing. Villagers are not an army and are not counted. */
export function countSoldiers(w: World, team: number): number {
  const u = w.units;
  let n = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== team) continue;
    if ((u.flags[i] & UF.Villager) !== 0) continue;
    n++;
  }
  return n;
}

/**
 * Point the host at the gates and sound the horn.
 *
 * Gates are chosen from the map's real gate list rather than by direction,
 * because the player has had a whole campaign to decide which approaches to
 * wall and which to leave open, and the attacker should be able to find the
 * open one. Two or three vectors, never one: a single column into a prepared
 * killing ground is not a siege, it is a queue.
 */
/**
 * Stand the sheltered host up inside its own town.
 *
 * Free, and deliberately so: these men were paid for on the strategic map and
 * charging for them again would mean a host is cheaper defending a field it
 * does not own than defending the town it was raised in. They are mustered at
 * the Hearth rather than on the curtain, because posting a company on a wall is
 * `Cmd.GarrisonCompany`'s job and it is the player's decision which face of the
 * wall matters — the men arrive as companies, in the middle of their own town,
 * with the whole build phase to be put somewhere.
 *
 * A regiment whose unit type is not in this settlement's roster is still
 * mustered. It marched here; refusing it because the town has no barracks that
 * drills it would be the settlement second-guessing the map about an army that
 * already exists.
 */
export function musterShelteredDefenders(w: World, regiments: readonly Regiment[]): number {
  let made = 0;
  const x = w.map.hearthX + 0.5;
  const y = w.map.hearthY + 4.5;
  for (const r of regiments) {
    let left = Math.max(0, Math.round(r.models));
    // A regiment is an accounting block and can be far larger than a company's
    // establishment, so it comes out as as many companies as it needs.
    for (let guard = 0; left > 0 && guard < 12; guard++) {
      const c = createCompany(w, r.typeIdx, Team.Player, x, y);
      const want = Math.min(left, c.maxSize);
      const got = fillCompany(w, c, want, x, y);
      if (got <= 0) break;
      made += got;
      left -= got;
    }
  }
  if (made > 0) {
    pushLog(
      w.events,
      `The host in the walls forms up in the square — ${made} men who were already here.`,
      LogTone.Good,
    );
  }
  return made;
}

export function beginHomeAssault(w: World, assault: HomeAssault): HomeAssaultSnapshot {
  const gateCount = Math.max(1, w.map.gates.length);
  const vectors = Math.min(gateCount, 2 + nextInt(w.rngDirector, 0, 2));
  const gates: number[] = [];
  for (let n = 0; n < vectors; n++) {
    let g = nextInt(w.rngDirector, 0, gateCount - 1);
    // Distinct vectors; walk forward rather than re-rolling so this terminates.
    for (let guard = 0; gates.includes(g) && guard < gateCount; guard++) {
      g = (g + 1) % gateCount;
    }
    if (!gates.includes(g)) gates.push(g);
  }

  w.incomingFaction = assault.factionId;
  w.incomingGates = gates;
  w.incomingModifiers = [];
  w.incomingComposition = assault.regiments
    .filter((r) => r.models > 0)
    .map((r) => ({ typeIdx: r.typeIdx, count: Math.max(1, Math.round(r.models)) }));
  w.incomingBudget = w.incomingComposition.reduce(
    (sum, e) => sum + unitType(e.typeIdx).threat * e.count,
    0,
  );

  /**
   * Before the snapshot, on purpose.
   *
   * `playerAtStart` is the denominator of `playerSurvive`, and `playerSurvive`
   * is what the campaign applies to the sheltered host as its losses. Muster
   * after the snapshot and the men who were hidden in the walls would count as
   * survivors of a fight they were never counted into, and a host of four
   * hundred could come out of a massacre reporting a hundred and ten percent.
   */
  musterShelteredDefenders(w, assault.defenders);

  const snapshot: HomeAssaultSnapshot = {
    assault,
    playerAtStart: Math.max(1, countSoldiers(w, Team.Player)),
    enemyAtStart: Math.max(
      1,
      w.incomingComposition.reduce((sum, e) => sum + e.count, 0),
    ),
  };

  pushLog(
    w.events,
    `${assault.nationName} are at the gates. This is the town you built; hold it.`,
    LogTone.Dire,
  );
  beginSiege(w);
  return snapshot;
}

/**
 * Has it been decided, and how?
 *
 * The city wins by clearing the field — `endSiege` puts the phase back to
 * Build, which is the same signal the campaign loop already waits on. It loses
 * the way it has always lost: the Hearth goes out.
 */
export function homeAssaultResult(w: World, snap: HomeAssaultSnapshot): HomeAssaultResult {
  const enemyLeft = countSoldiers(w, Team.Invader);
  const playerLeft = countSoldiers(w, Team.Player);
  const playerSurvive = Math.min(1, playerLeft / snap.playerAtStart);
  const enemySurvive = Math.min(1, enemyLeft / snap.enemyAtStart);

  if (w.gameOver) {
    return { decided: true, playerWon: false, playerSurvive, enemySurvive };
  }
  // Back in Build with the queue drained means the wave is done and the walls
  // held. `arrived` guards the tick the last group lands on, so this cannot
  // fire early — see `stepDirector`.
  if (w.phase === Phase.Build && w.spawnQueue.length === 0 && enemyLeft === 0) {
    return { decided: true, playerWon: true, playerSurvive, enemySurvive };
  }
  return { decided: false, playerWon: false, playerSurvive, enemySurvive };
}

/**
 * A line for the result sheet. The campaign wants numbers; the player wants to
 * know what it cost.
 */
export function assaultSummary(w: World, snap: HomeAssaultSnapshot): string {
  const stats = statsOf(w);
  const lost = snap.playerAtStart - countSoldiers(w, Team.Player);
  return (
    `${snap.assault.nationName} came with ${snap.enemyAtStart}. ` +
    `You buried ${Math.max(0, lost)} of your own, and ${stats.buildingsLost} buildings with them.`
  );
}
